import styles from './button.module.css';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { NextLink } from '@hub/common/NextLink/NextLink';

export const enum TYPE {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  CHECKED = 'checked',
  LIGHT = 'light',
}
export interface ButtonProps {
  text: string;
  type?: TYPE;
  disabled?: boolean;
  onClick?: () => void | Promise<void>;
  href?: string;
  className?: string;
  external?: boolean;
  useWindowNavigationHref?: boolean; // replaces href with onClick function
  useInternalRouting?: boolean; // wraps the a with a next/link
  download?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  minW?: boolean;
  rounded?: boolean;
  testId?: string;
  submit?: boolean;
  loading?: boolean;
  loaderText?: string;
  id?: string;
}

export const Button = ({
  text,
  type = TYPE.PRIMARY,
  disabled = false,
  onClick,
  href,
  className,
  external,
  useWindowNavigationHref,
  useInternalRouting,
  download,
  icon,
  iconPosition = 'left',
  minW = true,
  rounded = false,
  testId,
  submit = false,
  loading = false,
  loaderText = '',
  id,
}: ButtonProps) => {
  let typeClasses = '';

  switch (type) {
    case TYPE.SECONDARY:
      typeClasses = 'bg-gray-500 hover:bg-gray-600 text-white disabled:opacity-25';
      break;

    case TYPE.TERTIARY:
      typeClasses = disabled
        ? 'border text-gray-300 border-gray-200 bg-gray-100'
        : 'border text-igus-primary border-igus-primary';
      break;

    case TYPE.CHECKED:
      typeClasses = 'bg-igus-green text-white border border-igus-white disabled:opacity-25';
      break;

    case TYPE.LIGHT:
      typeClasses = 'bg-gray-50 hover:bg-gray-200 text-gray-600 disabled:opacity-25 px-6 rounded-[3px]';
      break;

    case TYPE.PRIMARY:
    default:
      typeClasses = 'bg-igus-primary hover:bg-igus-primary-600 text-white disabled:opacity-25';
      break;
  }
  const loader = () => (
    <div data-testid="button_loader" className="flex justify-start items-center h-full">
      <div
        className={`w-6 h-6 rounded-full animate-spin border-solid ${
          type === TYPE.TERTIARY ? 'border-[#F07D00]' : 'border-[white]'
        } border-t-transparent border-2`}
      />
      <p className="ml-8 text-sm">{loaderText}</p>
    </div>
  );
  const button = (
    <button
      data-testid={testId}
      id={id}
      onClick={onClick}
      disabled={disabled}
      type={submit ? 'submit' : 'button'}
      className={classNames(
        typeClasses,
        icon ? 'text-left' : 'justify-center',
        'my-3',
        'px-4',
        'py-2',
        'font-medium',
        'leading-8',
        'cursor-pointer',
        'outline-none',
        'focus:outline-none',
        'inline-flex',
        'whitespace-normal',
        'items-center',
        'w-full',
        rounded && 'rounded-full',
        minW && 'xl:min-w-button',
        styles.btn,
        'disabled:pointer-events-none',
        className,
        loading && 'relative'
      )}
    >
      {!loading && icon && iconPosition === 'left' && <div className="mr-2 flex items-center">{icon}</div>}
      {loading ? loader() : text}
      {!loading && icon && iconPosition === 'right' && <div className="ml-2 flex items-center">{icon}</div>}
    </button>
  );

  if (href) {
    const relAttr = [];
    if (external) {
      relAttr.push('noreferrer');
    }
    if (download) {
      relAttr.push('nofollow');
    }
    if (useInternalRouting) {
      // clientside routing using next link
      return (
        <NextLink href={href} data-testid={testId || 'button-internal-link'}>
          {button}
        </NextLink>
      );
    }
    // serverside or external routing
    if (useWindowNavigationHref) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
        <a
          onClick={() => {
            window.location.href = href;
          }}
          role="link"
          target={external ? '_blank' : undefined}
          rel={relAttr.length > 0 ? relAttr.join(' ') : undefined}
          download={download}
          data-testid={testId || 'button-download-link'}
        >
          {button}
        </a>
      );
    }
    return (
      // eslint can't figure out that we correctly set 'noreferrer' on external links
      // since it set dynamically
      // eslint-disable-next-line react/jsx-no-target-blank
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={relAttr.length > 0 ? relAttr.join(' ') : undefined}
        download={download}
        data-testid={testId || 'button-download-link'}
      >
        {button}
      </a>
    );
  }
  return button;
};
